/**
 * @Copyright 2014-2017 DataVoke, Inc. All rights reserved.
 */

"use strict";

import * as tools from '../utils/tools';
import {
    AggregateTypes,
    ButtonIconSrcTypes,
    ButtonTextTypes,
    EntityStates,
    PropertyDataType,
    TransActionTypes
} from '../utils/dvtypes';

const gridValueEventTypes = {
    valueChanged: 'valueChanged',
    writeUpdate: 'writeUpdate'
};

const UI = zebra.ui;

export const types = { gridValueEventTypes };

export const userLanguage = window.navigator.userLanguage || window.navigator.language;

export function isDirty(gv) {
    if (gv.property.Definition.RefViewID) {
        // This is a calculated analysis property, do not show dirty...AL
        return false;
    }
    else if (gv.property.Definition.NotSaved === true) {
        // This value is configured to not be saved, do not show dirty...AL
        return false;
    }
    else if (gv.dynamicValue.DataType === PropertyDataType.Binary) {
        // A binary CAN be saved if the entity state has been modified.
        if (gv.dynamicValueEdit != null
            && gv.dynamicValueEdit._State !== EntityStates.Unmodified) {
            return true;
        }

        //Otherwise... A binary value should never show dirty from this call (they are only saved by single uploads (This may change but for now this eliminates the save issues)...EK
        return false;
    }

    var mvc = null,
        cp = null;
    if (gv.parentRecord
        && gv.parentRecord.parentMVC) {
        mvc = gv.parentRecord.parentMVC;
    }
    if (mvc
        && mvc.columnProperties) {
        cp = mvc.columnProperties[gv.property.UID];
    }

    if (cp != null && cp.RecordTypeID != null) {
        if (!mvc.drivingView.Definition.RecordTypeIDs.find(rtLink => rtLink.UID === cp.RecordTypeID.UID)) {
            /*
             * This means that the column for this property in this view is not the same as the record type for the view.
             * This means that it is a linked property and cannot be dirty in this context...KB
             */
            return false;
        }
    }

    return gv.dynamicValueEdit != null
           || (
               (
                   gv.dynamicValue._State === EntityStates.NewObject
                   || gv.dynamicValue._State === EntityStates.CopyObject
               )
               && gv.dynamicValue.Value != null
               && gv.dynamicValue.Value !== ""
           );
}

export function valueUpdate(gv, dv) {
    // is this currently known as being edited?
    if (gv.dynamicValueEdit != null) {
        // do the values MATCH?
        if (dv.Value === gv.dynamicValue.Value) {
            // no longer in a pending state; value reverted.
            gv.dynamicValueEdit = null;
        }
        else {
            // passed value is our pending edit.
            gv.dynamicValueEdit = tools.deepClone(dv);
        }
    } else {
        // do the values NOT MATCH? NOTE: if they do match, nothing to do here.
        if (dv.Value !== gv.dynamicValue.Value) {
            // passed value is our pending edit.
            gv.dynamicValueEdit = tools.deepClone(dv);
        }
    }

    // we need to check if we are in a current transaction (and if so add this action to it)
    // or create a transaction for this value change...WR
    let mvc = null,
        monitor = null,
        transStartFlag = false;

    if (gv.parentRecord
        && gv.parentRecord.parentMVC) {
        mvc = gv.parentRecord.parentMVC;
    }
    if (mvc
        && mvc.transactionMonitor) {
        monitor = mvc.transactionMonitor;
    }

    if (monitor != null) {
        if (monitor.currentTransaction == null) {
            // if there is already a transaction, then this value change will be running under it.
            transStartFlag = true;
            monitor.startTransaction("gridValue.valueUpdate");
        }

        monitor.addAction(TransActionTypes.ValueChange, this,
            function (transCallback, obj, oldValue) {
                obj.displayProperties.setText(oldValue);
                transCallback();
            }, [gv, dv.Value]);
    }
    // only stop the transaction if this function was the entrypoint.
    if (transStartFlag) {
        monitor.stopTransaction();
    }
}

// This function calls all subscribers with the updated value (to show updates in form->grid / grid->form, etc.)...AL
export function writeUpdate(gv, val) {
    if (gv.subs.hasOwnProperty(gv.types.gridValueEventTypes.writeUpdate)) {
        var arr = gv.subs[gv.types.gridValueEventTypes.writeUpdate];
        for (var i in arr) {
            if (arr.hasOwnProperty(i)) {
                var r = arr[i][1].call(arr[i][0], val);
            }
        }
    }
}

export function updateUI(gv) {
    // the getter for gv.status will perform all the actions for getting the status from the grid value or grid record.
    let ignoreStatusStyling = false;
    if (gv.parentRecord != null
        && gv.parentRecord.parentMVC != null) {

        if (gv.parentRecord.parentMVC.isDisposed
            || gv.parentRecord.parentMVC.isClosing
            || gv.parentRecord.parentMVC.bulkInsert) {
            return;
        }

        let mvcView = gv.parentRecord.parentMVC.editView || gv.parentRecord.parentMVC.drivingView;
        ignoreStatusStyling = (mvcView && mvcView.Definition && mvcView.Definition.IgnoreStatusStyling) || false;
    }

    let _status = gv.status, c;

    // if this is the not initialized status, ignore the status and allow applying the style...PW
    if (_status.ValueStatusID === app.dv.cache.valueStatuses.notInitializedStatus.ValueStatusID) {
        ignoreStatusStyling = true;
    }

    // border property (border if it exists has to have the states)...BC
    if (gv.isDirty() === true && !ignoreStatusStyling) {
        let border = gv.displayProperties.zBorder;
        if (!(border && border.views
            && border.views.focuson && border.views.focuson.$hash$ === zebra.util.styleHelpers.standard.dirtyBorder.focuson.$hash$
            && border.views.focusoff && border.views.focusoff.$hash$ === zebra.util.styleHelpers.standard.dirtyBorder.focusoff.$hash$)) {
            // if the border isn't a viewset for dirty border, set it to one that is.
            gv.displayProperties.zBorder = new zebra.ui.ViewSet({
                'focusoff': zebra.util.styleHelpers.standard.dirtyBorder.focusoff,
                'focuson': zebra.util.styleHelpers.standard.dirtyBorder.focuson
            });
        }

        // activate the dirty border.
        gv.displayProperties.zBorder.activate("focuson");
    } else {
        gv.displayProperties.zBorder = null;
    }

    // background color
    if (_status != null && _status.Definition != null && !ignoreStatusStyling) {
        c = _status.Definition.Background;
        // skip alpha channel at BEGINNING of hex code...WR
        if (c.length > 7 && c.substring(0, 1) === "#") { c = "#" + c.substring(3, 9); }
        gv.displayProperties.zBackColor = c;
    }
    else {
        gv.displayProperties.zBackColor = null;
    }

    // font forecolor
    if (_status != null && _status.Definition != null && !ignoreStatusStyling) {
        c = _status.Definition.Foreground;
        // skip alpha channel at BEGINNING of hex code...WR
        if (c.length > 7 && c.substring(0, 1) === "#") { c = "#" + c.substring(3, 9); }
        gv.displayProperties.zForeColor = c;
    }
    else {
        gv.displayProperties.zForeColor = null;
    }

    // font weight
    if (_status != null && _status.Definition != null && _status.Definition.IsBold && !ignoreStatusStyling) {
        gv.displayProperties.zFontWeight = "bold";
    }
    else {
        gv.displayProperties.zFontWeight = "normal";
    }

    // status marker
    //if (gv.isAnalysisValue() === true) {
    //    gv.displayProperties.zStatusMarker = UI.palette.gvAnalysisColor;
    //} else

    //apply styling to required and readOnly statusMarker ...MD
    let style = null;
    if (gv.parentRecord != null) {
        style = gv.parentRecord.parentMVC.gridController.uiGrid.currentStyle;
    }
    if (gv.isRequiredFieldEmpty()) {
        gv.displayProperties.zStatusMarker = UI.palette.gvReqMissingColor;
        if (style != null) {
            let color = style.clientProperties.items.getVal("statusMarker_required_color");
            if (color != null) {
                gv.displayProperties.zStatusMarker = color;
            }
        }
    }
    else if (gv.displayProperties.isReadOnly()) {
        gv.displayProperties.zStatusMarker = UI.palette.gvReadOnlyColor;
        if (style != null) {
            let color = style.clientProperties.items.getVal("statusMarker_readOnly_color");
            if (color != null) {
                gv.displayProperties.zStatusMarker = color;
            }
        }
    }
    else {
        gv.displayProperties.zStatusMarker = null;
    }

    var ret = false;
    // call any event subscribers.
    if (gv.subs.hasOwnProperty(gv.types.gridValueEventTypes.valueChanged)) {
        var arr = gv.subs[gv.types.gridValueEventTypes.valueChanged];
        for (var i in arr) {
            if (arr.hasOwnProperty(i)) {
                // call the subscribed function (this object, event data, event context)
                var r = arr[i][1].call(arr[i][0], gv);
                if (r === true) { ret = true; }
            }
        }
    }
    return ret;
}

export function save(gv) {
    if (gv.dynamicValueEdit == null) { return true; }    // if no edit, nothing to save.
    gv.dynamicValue = gv.dynamicValueEdit;                // update to the edited value.
    gv.dynamicValueEdit = null;                            // clear the edited object (no edit pending).
    gv.valueChangeDV(gv.dynamicValue);                    // call the function to notify of the save change.
}

export function valueChangeDV(gv) {
    // call the parent record that the value has been saved.
    gv.dynamicValue.Value = gv.clientValue.dynamicValue.Value;
    gv.dynamicValue.TimeZoneOffset = gv.clientValue.dynamicValue.TimeZoneOffset;
    gv.dynamicValue._State = gv.clientValue.dynamicValue._State;
    gv.dynamicValueEdit = null;
    if (gv.parentRecord != null) {
        gv.parentRecord.valueChangeGV(gv);
    }
}

export function getTextValue(gv) {
    var v = "";
    // if dynamicValueEdit is null or its value is null, don't set variable "v".
    // NOTE: use of non-strict comparison here is INTENTIONAL; value may be null or undefined...WR
    if (gv.dynamicValueEdit != null) {
        if (gv.dynamicValueEdit.Value != null) {
            v = gv.dynamicValueEdit.Value;
        }
        else {
            v = "";
        }
    }
    else if (gv.dynamicValue.Value != null) {
        // we can assume that dynamicValue exists; only worry about value being null.
        v = (gv.dynamicValue.Value == null ? "" : gv.dynamicValue.Value);
    }
    else {
        v = "";
    }

    //NOTE: at this point, if dyanmicValue.Value is null or we do not have a dynamicValueEdit or dynamicValueEdit.Value is null, then our
    //        variable "v" will be an empty string. This is acceptable and expected.

    switch (gv.dynamicValue.DataType) {
        case PropertyDataType.Bit:
            // should be tri-state Boolean (true, false, null)...WR
            return String(getCheckValue(gv));
            break;
        case PropertyDataType.Integer:
            return getTextForInteger(v, gv);
            break;
        case PropertyDataType.Decimal:
            return getTextForDecimal(v, gv);
            break;
        case PropertyDataType.DateTime:
        case PropertyDataType.StaticDateTime:
            return getTextForDate(v, gv);
            break;
        case PropertyDataType.Binary:
            return getBinaryFilename(gv);
        case PropertyDataType.RTF:
        case PropertyDataType.InlineRTF:
        case PropertyDataType.Text:
        default:
            // if null value, we need to return an empty string...WR
            return (v == null) ? "" : String(v);
    }

    function getTextForInteger(value, gridVal) {
        if (value === "") {
            // if we don't have a value, we can't format anything...WR
            return "";  //changed from 0 to blank...BC
        }
        if (gridVal.colTextFormatObj != null) {
            return gridVal.colTextFormatObj.format(value);
        }
        else {
            var format = gridVal.property.Definition.DisplayFormat;
            if (format != null && format !== "") {
                var retVal = tools.NETtoJQueryFormatter(format, value);
                return retVal;
            }
            else {
                return Number(value).toLocaleString(userLanguage);
            }
        }
    }

    function getTextForDecimal(value, gridVal) {
        if (value === "") {
            // if we don't have a value, we can't format anything...WR
            return "";  //changed from 0.0 to blank...BC
        }

        if (gridVal.colTextFormatObj != null) {
            return gridVal.colTextFormatObj.format(value);
        }
        else {
            // The formatter will take a null, so we don't care if the format is null, use it...WR
            var format = gridVal.property.Definition.DisplayFormat;
            if (format == null || format === "") {
                format = "{d4}";
            }
            return tools.NETtoJQueryFormatter(format, value);
        }
    }

    function getTextForDate(value, gridVal) {
        value = getDateValue(gridVal);
        if (value != null && gridVal.colTextFormatObj != null) {
            return gridVal.colTextFormatObj.format(value);
        }
        else {
            return "";
        }
    }
}

export function setTextValue(gv, newValue) {
    // Here we handle blatantly invalid values for fields that do not allow all types of characters...TD
    // validateValue returns a validation object with two properties, value and response. value is the newly validated value
    // and response is whether this value should be allowed to used or not, response will be false if it needs to be reverted
    // otherwise true means the new value is acceptable...TD
    var validationResponse = tools.validateValue(gv.dynamicValue.DataType, newValue);


    if (validationResponse.response == false) {
        var dataType;
        switch (gv.dynamicValue.DataType) {
            case PropertyDataType.Bit:
                dataType = "Bit";
                break;
            case PropertyDataType.Decimal:
                dataType = "Decimal";
                break;
            case PropertyDataType.DateTime:
                dataType = "DateTime";
                break;
            case PropertyDataType.Integer:
                dataType = "Integer";
                break;
            case PropertyDataType.Text:
                dataType = "Text";
                break;
            case PropertyDataType.Binary:
                dataType = "Binary";
                break;
            case PropertyDataType.RTF:
                dataType = "RTF";
                break;
            case PropertyDataType.InlineRTF:
                dataType = "InlineRTF";
                break;
            case PropertyDataType.StaticDateTime:
                dataType = "StaticDateTime";
                break;
            default:
                dataType = "Unknown DataType"
        }
        app.browser.notifier.showToast(newValue + " is not a valid " + dataType + ", please correct before saving.", 5000);
        return;
    }
    newValue = validationResponse.value;

    // handle any specific format issues to set the values (this is display properties setters).
    switch (gv.dynamicValue.DataType) {
        case PropertyDataType.Bit:
            setCheckValue(gv, newValue);
            return;
            break;
        case PropertyDataType.DateTime:
        case PropertyDataType.StaticDateTime:
            setDateValue(gv, newValue);
            return;
            break;
        case PropertyDataType.Binary:
            //TODO: we need to interpret the filename and upload the file(?)
            break;
        case PropertyDataType.Decimal:
        case PropertyDataType.Integer:
            // Everything handled in validateValue for Decimal/Integer...TD
            break;
        case PropertyDataType.RTF:
        case PropertyDataType.InlineRTF:
        case PropertyDataType.Text:
        default:
            //TODO: Need to evaluate and ensure that there are no "\n" characters in the database data at some point...WR
            // this is to convert the new line characters used by JavaScript back to the carriage returns from database data.
            if (newValue != null) {
                newValue = newValue.toString().split("\n").join("\r");
            }

            break;
    }

    // if value was deleted ("") and our original value is null; those are the same thing!..WR
    if ((newValue == null
        || newValue === ""
        || newValue.toString().toLowerCase() === "no document")
        && gv.dynamicValue.Value == null) {
        newValue = null;
    }

    // we need to check if we are in a current transaction (and if so add this action to it)
    // or create a transaction for this value change...WR
    let mvc = null,
        monitor = null,
        transStartFlag = false;

    if (gv.parentRecord
        && gv.parentRecord.parentMVC) {
        mvc = gv.parentRecord.parentMVC;
    }
    if (mvc
        && mvc.transactionMonitor) {
        monitor = mvc.transactionMonitor;
    }

    if (monitor != null
        && monitor.currentTransaction == null) {
        // if there is already a transaction, then this value change will be running under it.
        transStartFlag = true;
        monitor.startTransaction("gridValue.setTextValue");
    }

    setValue(gv, newValue);

    // only stop the transaction if this function was the entrypoint.
    if (transStartFlag == true) {
        monitor.stopTransaction();
    }
}

/**
 * Attempt to get the boolean evaluation of the value of the specified grid value.
 * @param {GridValue} gv
 * @returns {boolean|null}
 */
export function getCheckValue(gv) {
    var v = null;
    if (gv.dynamicValueEdit != null) {
        v = gv.dynamicValueEdit.Value;
    }
    else if (gv.dynamicValue.Value != null) {
        v = gv.dynamicValue.Value;
    }

    switch (v) {
        case true:
        case false:
        case null:
            return v;        // if the value is any of our valid tri-state boolean (true, false, null), return it.
        case 1:
            return true;
        case 0:
            return false;
        default:
            return null;    // if the value is NOT a valid tri-state boolean, there may be an issue... return the default (null).
    }
}

/**
 * Attempt to set the value of the specified grid value using the newValue value as a boolean.
 * @param {GridValue} gv
 * @param {boolean} newValue
 */
export function setCheckValue(gv, newValue) {
    if (gv.isDisposed) {
        return;
    }
    if (gv.property.Definition.AllowNulls === true) {
        switch (newValue) {
            case true:
                newValue = true;
                break;
            case false:
                newValue = false;
                break;
            case null:
                newValue = null;
                break;
            default:
                switch (String(newValue).toLowerCase()) {
                    case "":
                    case "null":
                        newValue = null;
                        break;
                    case "false":
                    case "no":
                    case "n":
                    case "0":
                        newValue = false;
                        break;
                    default:
                        newValue = true;
                        break;
                }
                break;
        }
    }
    else {
        switch (newValue) {
            case true:
                newValue = true;
                break;
            case false:
                newValue = false;
                break;
            default:
                switch (String(newValue).toLowerCase()) {
                    case "":
                    case "null":
                    case "false":
                    case "no":
                    case "n":
                    case "0":
                        newValue = false;
                        break;
                    default:
                        newValue = true;
                        break;
                }
                break;
        }
    }

    // we need to check if we are in a current transaction (and if so add this action to it)
    // or create a transaction for this value change...WR
    let mvc = null,
        monitor = null,
        transStartFlag = false;

    if (gv.parentRecord
        && gv.parentRecord.parentMVC) {
        mvc = gv.parentRecord.parentMVC;
    }
    if (mvc
        && mvc.transactionMonitor) {
        monitor = mvc.transactionMonitor;
    }

    if (monitor != null
        && monitor.currentTransaction == null) {
        // if there is already a transaction, then this value change will be running under it.
        transStartFlag = true;
        monitor.startTransaction("setCheckValue");
    }

    setValue(gv, newValue);

    // only stop the transaction if this function was the entrypoint.
    if (transStartFlag == true) {
        monitor.stopTransaction();
    }
}

/**
 * Return what the toggled check value would be for the specified value (and if nulls are allowed for the value).
 * @param {boolean|null} value - The starting value for the toggle
 * @param {boolean} [flagAllowNulls = false] - (OPTIONAL) TRUE, Null will be a valid value toggled thorugh; FALSE (DEFAULT), do not toggle through Null.
 * @returns {boolean|null}
 */
export function getNextToggledCheckValue (value, flagAllowNulls = false) {
    if (flagAllowNulls) {
        switch (value) {
            case true:
                return false;

            case false:
                return null;

            case null:
            default:
                return true;

        }
    }
    else {
        switch (value) {
            case true:
                return false;

            case false:
            default:
                return true;

        }
    }
}

/**
 * Toggle the check value of the specified grid value; the grid value will be updated by this.
 * @param {GridValue} gv
 */
export function toggleCheckValue(gv) {
    if (gv.isDisposed) {
        return;
    }

    // toggle the value according to the property definition for the check value and set it back to the specified grid value object.
    gv.displayProperties.setCheckValue(getNextToggledCheckValue(gv.displayProperties.getCheckValue(), gv.property.Definition.AllowNulls));
}

export function getBinaryValue(gv) {
    var v = null, meta = "";
    if (gv.dynamicValueEdit != null) {
        v = gv.dynamicValueEdit.Value;
        meta = gv.dynamicValueEdit.ValueMetadata;
    }
    else {    // note: we don't worry if the gv.dynamicValue.Value is null; we expect it to be.
        v = gv.dynamicValue.Value;
        meta = gv.dynamicValue.ValueMetadata;
    }

    return FilenameFromBinary(meta);
}

export function setBinaryValue(gv, newValue) {
    // we need to check if we are in a current transaction (and if so add this action to it)
    // or create a transaction for this value change...WR
    let mvc = null,
        monitor = null,
        transStartFlag = false;

    if (gv.parentRecord
        && gv.parentRecord.parentMVC) {
        mvc = gv.parentRecord.parentMVC;
    }
    if (mvc
        && mvc.transactionMonitor) {
        monitor = mvc.transactionMonitor;
    }

    if (monitor != null
        && monitor.currentTransaction == null) {
        // if there is already a transaction, then this value change will be running under it.
        transStartFlag = true;
        monitor.startTransaction("gridValue.setBinaryValue");
    }

    setValue(gv, newValue);

    // only stop the transaction if this function was the entrypoint.
    if (transStartFlag == true) {
        monitor.stopTransaction();
    }
}

export function getDateValue(gv) {
    var v = null;
    /**
     * We need to use the "UTC" date in the dynamicValue.Value to get a new date. Since
     * this will re-apply the current timezone of the client, for normal dates we are done.
     * For static dates, we need to strip the local timezone and apply the time zone
     * offset in the dynamicValue (or dynamicValueEdit - which should be the current one).
     */
    if (gv.dynamicValueEdit != null) {
        // the dynamicValueEdit object may be not null (there is an edit), but the edit itself may be null.
        if (gv.dynamicValueEdit.Value != null) {
            if (gv.dynamicValue.DataType === PropertyDataType.DateTime) {
                v = new Date(gv.dynamicValueEdit.Value).addHours(0 - (gv.dynamicValueEdit.Value.getTimezoneOffset() / 60));
            }
            else {
                v = new Date(gv.dynamicValueEdit.Value).addHours(gv.dynamicValueEdit.TimeZoneOffset);
            }
        }
    }
    else if (gv.dynamicValue.Value != null) {
        var gvDV = gv.dynamicValue;
        //If this is null, this is a "date value" from the server (they don't carry methods with them)...BC
        if(gvDV.Value.getTimezoneOffset == null){
            gvDV.Value = new Date(gvDV.Value);
            if(gvDV.DataType === PropertyDataType.StaticDateTime) {
                //Need to do the inverse for static date, otherwise the first value will show up incorrect and then fix on save...BC
                return new Date(gvDV.Value).addHours(0 - (gvDV.Value.getTimezoneOffset() / 60));
            }
            return gvDV.Value.addHours(0 + (gvDV.Value.getTimezoneOffset() / 60));
        }
        //Otherwise, proceed as normal...BC
        if (gvDV.DataType === PropertyDataType.DateTime) {
            v = new Date(gvDV.Value).addHours(0 - (gvDV.Value.getTimezoneOffset() / 60));
        }
        else {
            v = new Date(gvDV.Value).addHours(gvDV.TimeZoneOffset);
        }
    }

    return v;
}

export function setDateValue(gv, newValue) {
    var applyTz = true,
        displayText = gv.displayProperties.Text;
    /**
     * NOTE: Note that we DO NOT account for the data type here; static or just date/time.
     * The reason is that at this point, the TimeZoneOffset property IS THE SAME as the
     * time zone offset of the Date structure - so regardless we can use it for both data
     * types.
     */

    //numbers entered in formulas will come in as type number, must be converted to String to be parsed correctly...TD
    if (typeof newValue === "number") {
        newValue = newValue.toString();
    }

    if (newValue instanceof Date) {
        // this is already a date, so we only need to de-reference so that we don't manipulate the value provided.
        newValue = new Date(newValue)
    }
    else if (newValue === displayText) {
        if (newValue !== "") {
            // need to get the saved or current edited value.
            newValue = (gv.dynamicValueEdit == null ? gv.dynamicValue.Value : gv.dynamicValueEdit.Value);
            applyTz = false;

        }
        //NOTE: at this point, if newValue is "", then it will remain that.
        //        it will be set to null later on below.
    }
    else if (newValue !== ""
        && (typeof newValue === 'string' || newValue instanceof String)
        && newValue !== displayText) {

        try {
            if (newValue.contains("GMT")
                && newValue.contains("(")
                && newValue.contains(")")
                && newValue.length > 25) {
                newValue = new Date(newValue);
            }
            else {
                try {
                    if (/^\d{4}$/.test(newValue)) {
                        // "YYYY" strings will be parsed as ISO-8601 format in UTC, instead of the expected local time. Force RFC-2822 format.
                        // EX: new Date('2018') => Sun Dec 31 2017 19:00:00 GMT-0500 (Eastern Standard Time)
                        newValue = `1/1/${newValue}`;
                    }
                    else if (/^\d{4}(-\d{2}){1,2}$/.test(newValue)) {
                        // "YYYY-MM" and "YYYY-MM-DD" strings will be parsed as ISO-8601 format in UTC, instead of the expected local time. Force RFC-2822 format.
                        // EX: new Date('2018-11-12') => Sun Nov 11 2018 19:00:00 GMT-0500 (Eastern Standard Time)
                        newValue = newValue.replace(/-/g,"/");
                    }
                    let subtest = new Date(newValue);
                    if (subtest.toString() === "Invalid Date") {
                        //If the original try is invalid try again by parsing the value first...BC

                        if (newValue.contains(":")) {
                            //pm causes the date to fail, delete the string..MD
                            let timeValues = newValue.split(":"),
                                sHours,
                                sMinutes,
                                sSeconds = 0;
                            if(timeValues.length >=1 ){
                                sHours = parseInt(timeValues[0]);
                            }
                            if(timeValues.length >= 2 ){
                                sMinutes = parseInt(timeValues[1]);
                            }
                            if(timeValues.length >=3 ){
                                sSeconds = parseInt(timeValues[2]);
                            }

                            //create pm times
                            if (newValue.contains("PM")|| newValue.contains("pm")) {
                                //remove pm string or new date will fail
                                newValue = newValue.replace("PM", "");
                                newValue = newValue.replace("pm", "");
                                //pm times
                                if (sHours < 12) {
                                    sHours = sHours + 12;
                                }
                            }
                            //create am times
                            else if (newValue.toString().toLowerCase().contains("am")) {
                                //remove am string or new date will fail
                                newValue = newValue.replace("AM", "");
                                newValue = newValue.replace("am", "");
                                if (sHours === 12) {
                                    sHours = 0;
                                }
                            }
                            //try to create a new date with the provided time..MD
                            newValue = new Date(Date.parse(newValue));
                            newValue.setHours(sHours, sMinutes, sSeconds);
                        }
                        else {
                            newValue = new Date(Date.parse(newValue));
                        }
                    }
                    else {
                        //newValue = new Date(subtest);
                        //subTest is a valid Date, check if 12:## am needs to be fixed MD...BC
                        let sHours = subtest.getHours();
                        if (sHours === 12 && newValue.toString().toLowerCase().contains("am")) {
                            subtest.setHours(0);
                        }
                        newValue = subtest;
                    }
                }
                catch (exp) {
                    newValue = new Date(Date.parse(newValue));
                }
            }
        }
        catch (ex) {
            throw new Error("Date Times must be in correct format - Error: " + ex.message);
        }
    }

    if (newValue === "") {
        // value has been deleted or blanked; this is a null, not a blank string for a date time.
        newValue = null;
    }

    if (newValue != null && applyTz) {
        const originalOffset = newValue.getTimezoneOffset();
        // A date set here can never be UTC in JS, ensure we offset based on the client's Time Zone...EK
        newValue.addMinutes(newValue.getTimezoneOffset());

        const newOffset = newValue.getTimezoneOffset();
        newValue.addMinutes(newOffset - originalOffset);
    }

    // we need to check if we are in a current transaction (and if so add this action to it)
    // or create a transaction for this value change...WR
    let mvc = null;
    if (gv.parentRecord
        && gv.parentRecord.parentMVC) {
        mvc = gv.parentRecord.parentMVC;
    }
    let monitor = mvc != null ? mvc.transactionMonitor : null;
    let transStartFlag = false;

    if (mvc != null
        && mvc.bulkInsert == false
        && monitor != null
        && monitor.currentTransaction == null) {
        // if there is already a transaction, then this value change will be running under it.
        transStartFlag = true;
        monitor.startTransaction("setDateValue");
    }

    setValue(gv, newValue);

    // only stop the transaction if this function was the entrypoint.
    if (transStartFlag == true) {
        monitor.stopTransaction();
    }
}

export function getIsReadOnly(gv) {
    if (gv == null) { return false; }
    if (gv.isHistoricalValue === true) { return true; }

    if (gv.isAnalysisValue() === true) { return true; }

    if (gv.forceReadOnly === true) { return true; }

    if (gv.property.Definition.ERESDate === true ) { return true; }

    // Calculated field, must be read only...AL
    // Unless it is an "on create only" formula then it needs to be editable...TD
    if (gv.property.Definition.Formula != null
        && gv.property.Definition.Formula !== ""
        && !gv.property.Definition.IsCalculateExpressionOnlyOnceEnabled) {
        return true;
    }

    let gvStatus = gv.getStatus();
    if (gvStatus
        && gvStatus.Definition
        && gvStatus.Definition.IsInterfaceLocked) {
        return true;
    }

    let isReadOnlyParentMVC = null;
    if (gv.parentRecord
        && gv.parentRecord.parentMVC) {
        isReadOnlyParentMVC = gv.parentRecord.parentMVC;
    }

    if (isReadOnlyParentMVC != null) {
        if (isReadOnlyParentMVC.isSelectionView) {
            // selection views make all values in the grid and form read-only...WR
            return true;
        }

        let columns = isReadOnlyParentMVC != null ? isReadOnlyParentMVC.columnProperties : null,
            cp = columns != null ? columns[gv.property.UID] : null;

        if (cp != null) {
            // if the column property for this is read only, so are we.
            if (cp.IsReadOnly) {
                return true;
            }

            // if the record type id for the column is not the same as the view, then this is a linked property.
            if (cp.RecordTypeID) {
                if (isReadOnlyParentMVC.drivingView
                    && isReadOnlyParentMVC.drivingView.Definition
                    && Array.isArray(isReadOnlyParentMVC.drivingView.Definition.RecordTypeIDs)
                    && !isReadOnlyParentMVC.drivingView.Definition.RecordTypeIDs.find(rtLink => rtLink.UID === cp.RecordTypeID.UID)) {
                    return true;
                }
            }
        }

        return !app.dv.mvc.userCanEditValue(gv.parentRecord.parentMVC, gv);
    }

    return false;
}

export function getIsAnalysisValue(gv) {
    if (gv == null) {
        return false;
    }

    let isAnalysisValueMvc = null;
    if (gv.parentRecord
        && gv.parentRecord.parentMVC) {
        isAnalysisValueMvc = gv.parentRecord.parentMVC;
    }

    let columns = isAnalysisValueMvc != null ? isAnalysisValueMvc.gridColumns : null,
        col = columns != null ? columns.getVal(gv.property.PropertyID) : null;

    return col != null && col.analysisRefMvc != null;
}

export function getIsRequiredFieldEmpty(gv) {
    // if it is required and empty, then yes, we need it.
    return getIsRequired(gv) && gv.displayProperties.getText() === "";
}

export function getIsRequired(gv) {
    if (gv == null) { return false; }

    //If this value is historical, bypass this property logic, and just return false ... EK
    if (gv.isHistoricalValue === true) { return false; }

    // if this is a record code property and there is no value, then we need to show this as well.
    if (isRecordCode(gv) === true) { return true; }

    // If the property is required and has no value, return true...AL
    let propIsRequired = gv.property.Definition.IsRequired;

    // Or if the view property is required and has no value, return true...AL
    // Grab a reference to the ViewProperty...AL
    let isRequiredMvc = null;
    if (gv.parentRecord
        && gv.parentRecord.parentMVC) {
        isRequiredMvc = gv.parentRecord.parentMVC;
    }
    let columns = isRequiredMvc != null ? isRequiredMvc.columnProperties : null,
        cp = columns != null ? columns[gv.property.UID] : null;

    if (cp == null) {
        // no column property, this property isn't really in this record; simply return if the property is defined to be required.
        return propIsRequired;
    }

    let sameRecord = true;
    if (cp.RecordTypeID) {
        // if this is a linked property, then it is not really required for _this_ record...
        // if the record type id for the column is not the same as the view, then this is a linked property.
        if (isRequiredMvc
            && isRequiredMvc.drivingView
            && isRequiredMvc.drivingView.Definition
            && Array.isArray(isRequiredMvc.drivingView.Definition.RecordTypeIDs)) {
            sameRecord = isRequiredMvc.drivingView.Definition.RecordTypeIDs.findIndex(rtLink => rtLink.UID === cp.RecordTypeID.UID) > -1;
        }
    }

    // return if the item is required or not.
    return cp.IsRequired || (sameRecord && propIsRequired);
}

export function getIsSpellchecked(gv) {
    if (gv
        && gv.property
        && gv.property.Definition) {
        return !!gv.property.Definition.IsSpellCheckEnabled;
    }
    return false;
}

/**
 * Get the tooltip text for this grid value
 * @param {GridValue} gv - The grid value to generate the tooltip text for
 * @returns {string} The generated tooltip text
 */
export function getTooltipText(gv) {
    // No hint for historical values...AL
    if (gv.isHistoricalValue) return "";

    const view = gv.parentRecord && gv.parentRecord.parentMVC && (gv.parentRecord.parentMVC.editView || gv.parentRecord.parentMVC.drivingView);
    const hideStatusTooltip = view && view.Definition && view.Definition.HideStatusTooltipText;

    // this is our returned string for the status hint.
    let tooltipText = "";
    if (gv.dynamicValue.DataType === PropertyDataType.Binary || gv.dynamicValue.DataType === PropertyDataType.RTF) {
        const metaData = gv.dynamicValueEdit ? gv.dynamicValueEdit.ValueMetadata : gv.dynamicValue.ValueMetadata;
        if (metaData) {
            // use a parse that supports NaN as some metadata may contain it; this would error in regular JSON.parse()
            const nodes = JSON.parseMore(metaData);
            let size, lastModified;
            for (let i in nodes) {
                if (nodes.hasOwnProperty(i)) {
                    const data = nodes[i];
                    switch (data.Key) {
                        case "FileSize":
                            size = tools.displayFileSize(data.Value);
                            break;
                        case "FileDate":
                            const date = new Date(String(data.Value));
                            if (date && date.toString() !== 'Invalid Date') {
                                lastModified = date.toLocaleString();
                            }
                            break;
                    }
                }
            }
            if (size != null && lastModified != null) {
                tooltipText = `Size: ${size}\nModified Date: ${String(lastModified)}`;
            }
        }
        if (tooltipText === "") {
            tooltipText = "No file data available.";
        }
    }
    else {
        if (!hideStatusTooltip) {
            if (gv.status) {
                const gvStatus = gv.getStatus();
                tooltipText = "Assigned Status: " + gvStatus.Definition.Name + " (" + gvStatus.Definition.Description + ")";
            }
            tooltipText += (tooltipText !== "" ? '\n' : '') + "State: ";
            if ((gv.dynamicValue._State === EntityStates.NewObject && !gv.dynamicValue.value) || gv.property.Definition.IsAnalysis) {
                // New object with no value, or analysis values get no status hint...AL
                tooltipText += "None";
            }
            else if (gv.dynamicValue._State === EntityStates.Unmodified) {
                tooltipText += "Saved";
            }
            else {
                tooltipText += "Save Required";
            }
        }
    }

    if (!hideStatusTooltip) {
        if (gv.isHistoricalValue) {
            tooltipText += `${tooltipText !== '' ? '\n' : ''}Value is historical (should not be edited).`;
        }
        else if (gv.property.Definition.IsAnalysis) {
            tooltipText += `${tooltipText !== '' ? '\n' : ''}Value is an analysis value and cannot be edited.`;
        }
        else if (getIsReadOnly(gv)) {
            tooltipText += `${tooltipText !== '' ? '\n' : ''}Value is Read-Only.`;
        }
        else if (getIsRequiredFieldEmpty(gv)) {
            tooltipText += `${tooltipText !== '' ? '\n' : ''}Field is required, but has no data entered.`;
        }
    }

    return tooltipText;
}

export function resetValue(gv) {
    if (gv.dynamicValueEdit != null) {
        // the value was reset back to the original; no effective change.
        gv.dynamicValueEdit = null;

        if (gv.dynamicValue._State !== EntityStates.NewObject)
            gv.dynamicValue._State = EntityStates.Unmodified;

        gv.updateUI();

        if (gv.parentRecord != null && gv.parentRecord.parentMVC != null) {
            // update the client record code property if this is the code property and it has changed.
            if (gv.property.PropertyID === gv.parentRecord.parentMVC.primaryRecordType.Definition.CodePropertyID) {
                gv.parentRecord.clientRecord.Record.Code = gv.dynamicValue.Value;
                if (gv.dynamicValue._State !== EntityStates.NewObject) {
                    gv.parentRecord.clientRecord.Record.Status = gv.dynamicValue.Status;
                }
            }
        }
    }
}

function FilenameFromBinary(metaData) {
    if (metaData !== "") {
        // use a parse that supports NaN as some metadata may contain it; this would error in regular JSON.parse()
        var nodes = JSON.parseMore(metaData);
        for (var i in nodes) {
            if (nodes.hasOwnProperty(i)) {
                var data = nodes[i];
                if (data.Key === "FileName") {
                    if (data.Value === "Upload Document" || data.Value === "No Document") { break; }
                    return data.Value;
                }
            }
        }
    }
    return "No Document";
}

export function getBinaryFilename(gv) {
    if (gv.dynamicValueEdit != null) {
        return FilenameFromBinary(gv.dynamicValueEdit.ValueMetadata);
    } else {
        return FilenameFromBinary(gv.dynamicValue.ValueMetadata);
    }
}

export function getNumberValue(gv) {
    if (gv.dynamicValueEdit != null) {
        return Number(gv.dynamicValueEdit.Value);
    } else {
        return Number(gv.dynamicValue.Value);
    }
}

export function isRecordCode(gv) {
    if (gv == null) { return false; }

    let codePropID = null;
    if (gv.parentRecord
        && gv.parentRecord.parentMVC
        && gv.parentRecord.parentMVC.primaryRecordType
        && gv.parentRecord.parentMVC.primaryRecordType.Definition) {
        codePropID = gv.parentRecord.parentMVC.primaryRecordType.Definition.CodePropertyID;
    }
    return (codePropID != null && gv.property.UID === codePropID.UID);
}

export function recalcExpressions(gv) {
    // determine if we have access to the MVC (or if we are disposing).
    let list = null;
    if (gv
        && gv.parentRecord
        && gv.parentRecord.parentMVC) {
        list = gv.parentRecord.parentMVC.gridColumns
    }
    if (list == null) {
        return;
    }

    //If this is an ERES Date property, we can't set it via expression...BC
    if(gv.property.Definition.ERESDate === true) {
        return;
    }

    //DF-1359/1360 - Check the status of the parent record, if it is marked as deleted then we don't want to evaluate any expressions (property or status) ... EK
    //... but ONLY if data retention is turned on!
    if (app.dv.cache.session.configEntity.ConfigValue.tuneOptionSettings.KeepDeletedRecords
        && gv.parentRecord.getStatus() != null
        && app.dv.cache.valueStatuses.markedAsDeletedStatus != null
        && gv.parentRecord.getStatus().UID.toLowerCase() === app.dv.cache.valueStatuses.markedAsDeletedStatus.UID.toLowerCase()) {
        return;
    }

    //Scan all columns to see if the updated value will trigger another value's expression...EK
    list.Values.forEach((col) => {
        // update the value expression for this column, if it is dependent on the specified gridValue...WR
        // as long as the current column isn't the specified gridValue (not dependent on itself) (DF-3334)...AM
        if (col.expression != null) {
            let gridVal = gv.parentRecord.gridValues.getVal(col.property.PropertyID);
            if (gridVal != null) {
                let val = (gridVal.dynamicValueEdit == null) ? gridVal.dynamicValue : gridVal.dynamicValueEdit;
                if (!col.property.Definition.IsCalculateExpressionOnlyOnceEnabled || val._State === EntityStates.NewObject) {
                    col.expression.propertyIDs.some((id) => {
                        if (id.UID === gv.property.UID && gv.property.UID !== col.property.UID) {
                            try {
                                let value = col.expression.evaluate(gv.parentRecord.parentMVC.drivingView.ViewID, gv.parentRecord);
                                gridVal.displayProperties.setText(value);
                            }
                            catch (e) {
                                if (app.designer) {
                                    app.browser.notifier.showToast(`Error evaluating value expression (Resetting value):<br/><br/>${col.expression.rawFormula}<br/><br/>Error: ${e.message}`);
                                }
                            }
                            return true;
                        }
                        return false;
                    });
                }
            }
        }

        // update the status expression for this column, if it is depended on the specified gridValue...WR
        // NOTE: this may or may not change the actual status for this property, depending on the order the status expression for this changed value property is.
        col.statusExpressions.some((exp) => {
            let flagEvaluated = false;
            exp.propertyIDs.some((id) => {
                if (id.UID === gv.property.UID){
                    app.dv.helpers.expression.evaluateStatus(gv.parentRecord.parentMVC.drivingView.ViewID, gv.parentRecord, col.statusExpressions, gv.parentRecord.parentMVC.primaryRecordType.Definition.CodePropertyID);
                    flagEvaluated = true;
                    return true;
                }
                return false
            });
            return flagEvaluated;
        });
    });

    let updateSubStyles = false;

    /**
     * Update the sub controllers for the form controller specified for any labels whose value contains the specified value.
     * @param {LiteFormController} fc - The form controller whose sub controllers may need updates.
     * @param {gridValue} val - The grid value that was updated.
     */
    function updateSubControllers(fc, val) {
        //Ensure we have a form controller
        if (fc == null){
            return;
        }

        // check styles for this controller.
        if (fc.mvc.StyleProperties.indexOf(gv.property.UID) > -1) {
            updateSubStyles = true;
        }

        if (fc.subControllers != null) {
            fc.subControllers.Values.forEach((controller) => {
                // we only want to check this to update *IF* the record on the controller is the same as the record that has the updated value.
                if (tools.get(controller, "record.clientRecord.Record") != null
                    && controller.record.clientRecord.Record.RecordID === val.parentRecord.clientRecord.Record.RecordID) {

                    //update the button texts that are generated via expression...AM
                    controller.formActions.Values.forEach((uigButton) => {
                        uigButton.uiControl.forEach((uiButton) => {
                            if (uiButton.expr != null && uiButton.definition.TextType === ButtonTextTypes.Expression && uiButton.expr.propertyIDs.some(o => o.UID === gv.property.UID)) {
                                let labelText = (uiButton.actionButton.label && uiButton.actionButton.label.getValue()) || "";
                                try {
                                    labelText = uiButton.expr.evaluate(controller.mvc.drivingView.ViewID, controller.record);
                                }
                                catch (e) {
                                    if (app.designer) {
                                        app.browser.notifier.showToast(`Error evaluating button text expression:<br/><br/>${uiButton.expr.rawFormula}<br/><br/>Error: ${e.message}`);
                                    }
                                }

                                uiButton.actionButton.setLabel(labelText);
                            }

                            // Update any icon expression
                            if (uiButton.iconExpr != null && uiButton.definition.IconSrcType === ButtonIconSrcTypes.Expression) {
                                let iconValue = '',
                                    finishIcon = (imgPan) => {
                                        if (uiButton == null || uiButton.actionButton == null || controller.mvc == null || controller.mvc.isClosing) {
                                            // This function is called asynchronously, so we need to check if the mvc had closed or button was removed before the image loaded
                                            return;
                                        }

                                        if (imgPan) {
                                            imgPan.view.color = app.theme.userCommon.buttons.iconBackgroundColor;
                                        }

                                        uiButton.actionButton.setImage(imgPan);

                                        //apply style
                                        if (uiButton.currentStyle) {
                                            uiButton.applyStyle(uiButton.currentStyle, controller);
                                        }
                                    };

                                try {
                                    iconValue = uiButton.iconExpr.evaluate(controller.mvc.drivingView.ViewID, controller.record);
                                }
                                catch (e) {
                                    if (app.designer) {
                                        app.browser.notifier.showToast(`Error evaluating button icon expression:<br/><br/>${uiButton.iconExpr.rawFormula}<br/><br/>Error: ${e.message}`);
                                    }
                                    finishIcon();
                                }

                                if (iconValue instanceof GridValue) {
                                    // This expr was bound to a property grid value, allow the controller to set and bind the icon
                                    controller.dataLoaded();
                                }
                                else {
                                    // We assume the expr value is a URL, pull the image and set the icon
                                    tools.getImagePanByURL(iconValue, 20, 20).then((imgPanFromURL) => {
                                        finishIcon(imgPanFromURL);
                                    });
                                }
                            }
                        });
                    });

                    // get each UI Group from the formLabels dictionary
                    controller.formLabels.Values.forEach((uig) => {
                        // get each array of UI controls from the UI Group
                        uig.uiControl.forEach((label) => {
                            // check if the label control has an expression, and if it has this property in the expression.
                            if (label.textLabel && label.expr && label.expr.propertyIDs.some(o => o.UID === gv.property.UID)) {
                                // we need to recalculate the label control's text.
                                let labelValue = label.labelTimeoutValue || label.textLabel.getValue();
                                try {
                                    labelValue = label.expr.evaluate(controller.mvc.drivingView.ViewID, controller.record);
                                }
                                catch (e) {
                                    if (app.designer) {
                                        app.browser.notifier.showToast(`Error evaluating label expression (Not updating value):<br/><br/>${label.expr.rawFormula}<br/><br/>Error: ${e.message}`);
                                    }
                                }
                                if (label.TextFormatterObj != null) {
                                    labelValue = label.TextFormatterObj.format(labelValue);
                                }
                                if (label.labelTimeoutValue != null) {
                                    // The form controller is still waiting for this control to be valid, update the value it will set once it is valid
                                    label.labelTimeoutValue = labelValue;
                                }
                                else {
                                    label.textLabel.setValue(String(labelValue));
                                }
                            }
                        });
                    });

                    controller.formWidgets.Values.forEach((uig) => {
                        uig.uiControl.forEach((widget) => {
                            if (widget.expr != null && widget.expr.propertyIDs.some(p => p.UID === gv.property.UID)) {
                                // we need to recalculate the expression for the widget.
                                try {
                                    let calcValue = widget.expr.evaluate(controller.mvc.drivingView.ViewID, controller.record);
                                    widget.setValue(calcValue);
                                }
                                catch (e) {
                                    if (app.designer) {
                                        app.browser.notifier.showToast(`Error evaluating field (widget) expression (Not updating value):<br/><br/>${widget.expr.rawFormula}<br/><br/>Error: ${e.message}`);
                                    }
                                }
                            }
                        });
                    });
                }

                // check sub-controllers (of the sub-controller, etc.).
                updateSubControllers(controller, val);
            });
        }
    }

    // update any labels within the context of this value's MVCs (there may be multiple) that utilize this value.
    gv.clientValue.localValues.forEach((val) => {
        let mvc = val.parentRecord.parentMVC,
            fc = mvc.formController,
            record = null;

        // if there is no form controller for this parent record's MVC instance, there is no visible label control to update.
        if (fc != null
            // We are attempting to update a linked drop down source, which will not have any labels so do not do anything
            && !mvc.isDropDown) {

            // we only want to update labels if THIS record is THE selected record for the MVC.
            if (mvc.selectedRecord
                && mvc.selectedRecord.clientRecord
                && mvc.selectedRecord.clientRecord.Record) {
                record = mvc.selectedRecord.clientRecord.Record
            }

            if (record != null) {
                if (val.parentRecord.clientRecord.Record.RecordID === mvc.selectedRecord.clientRecord.Record.RecordID) {
                    // this is a selected record for this value; we need to update the labels.

                    //update any button related expressions...AM
                    fc.formActions.Values.forEach((uigButton) => {
                        uigButton.uiControl.forEach((uiButton) => {
                            // Update the button text expression
                            if (uiButton.expr != null && uiButton.definition.TextType === ButtonTextTypes.Expression && uiButton.expr.propertyIDs.some(o => o.UID === gv.property.UID)) {
                                let labelText = (uiButton.actionButton.label && uiButton.actionButton.label.getValue()) || "";
                                try {
                                    labelText = uiButton.expr.evaluate(mvc.drivingView.ViewID, fc.record);
                                }
                                catch (e) {
                                    if (app.designer) {
                                        app.browser.notifier.showToast(`Error evaluating button text expression:<br/><br/>${uiButton.expr.rawFormula}<br/><br/>Error: ${e.message}`);
                                    }
                                }

                                uiButton.actionButton.setLabel(labelText);
                            }

                            // Update the button icon expression
                            if (uiButton.iconExpr != null && uiButton.definition.IconSrcType === ButtonIconSrcTypes.Expression) {
                                let iconValue = '',
                                    finishIcon = (imgPan) => {
                                        if (uiButton == null || uiButton.actionButton == null || mvc == null || mvc.isClosing) {
                                            // This function is called asynchronously, so we need to check if the mvc had closed or button was removed before the image loaded
                                            return;
                                        }

                                        if (imgPan) {
                                            imgPan.view.color = app.theme.userCommon.buttons.iconBackgroundColor;
                                        }

                                        uiButton.actionButton.setImage(imgPan);
                                        uiButton.actionButton.updateContentPosition(uiButton.definition.ContentAlignment, uiButton.definition.IconPosition);

                                        //apply style
                                        if (uiButton.currentStyle) {
                                            uiButton.applyStyle(uiButton.currentStyle, fc);
                                        }
                                    };

                                try {
                                    iconValue = uiButton.iconExpr.evaluate(mvc.drivingView.ViewID, fc.record);
                                }
                                catch (e) {
                                    if (app.designer) {
                                        app.browser.notifier.showToast(`Error evaluating button icon expression:<br/><br/>${uiButton.iconExpr.rawFormula}<br/><br/>Error: ${e.message}`);
                                    }
                                    finishIcon();
                                }

                                if (iconValue instanceof GridValue) {
                                    // This expr was bound to a property grid value, allow the controller to set and bind the icon
                                    fc.dataLoaded();
                                }
                                else {
                                    // We assume the expr value is a URL, pull the image and set the icon
                                    tools.getImagePanByURL(iconValue, 20, 20).then((imgPanFromURL) => {
                                        finishIcon(imgPanFromURL);
                                    });
                                }
                            }
                        });
                    });

                    // get each UI Group from the formLabels dictionary
                    fc.formLabels.Values.forEach((uig) => {
                        // get each array of UI controls from the UI Group
                        uig.uiControl.forEach((label) => {
                            // check if the label control has an expression, and if it has this property in the expression.
                            if (label.expr && label.expr.propertyIDs.some(o => o.UID === gv.property.UID)) {
                                // we need to recalculate the label control's text.
                                let labelValue = label.labelTimeoutValue || label.textLabel.getValue();
                                try {
                                    labelValue = label.expr.evaluate(mvc.drivingView.ViewID, fc.record);
                                }
                                catch (e) {
                                    if (app.designer) {
                                        app.browser.notifier.showToast(`Error evaluating label expression (Not updating value):<br/><br/>${label.expr.rawFormula}<br/><br/>Error: ${e.message}`);
                                    }
                                }
                                if (label.TextFormatterObj != null) {
                                    labelValue = label.TextFormatterObj.format(labelValue);
                                }
                                if (label.labelTimeoutValue != null) {
                                    // The form controller is still waiting for this control to be valid, update the value it will set once it is valid
                                    label.labelTimeoutValue = labelValue;
                                }
                                else {
                                    label.textLabel.setValue(String(labelValue));
                                }
                            }
                        });
                    });

                    fc.formWidgets.Values.forEach((uig) => {
                        uig.uiControl.forEach((widget) => {
                            if (widget.expr != null && widget.expr.propertyIDs.some(p => p.UID === gv.property.UID)) {
                                // we need to recalculate the expression for the widget.
                                try {
                                    let calcValue = widget.expr.evaluate(mvc.drivingView.ViewID, fc.record);
                                    widget.setValue(calcValue);
                                }
                                catch (e) {
                                    if (app.designer) {
                                        app.browser.notifier.showToast(`Error evaluating field (widget) expression (Not updating value):<br/><br/>${widget.expr.rawFormula}<br/><br/>Error: ${e.message}`);
                                    }
                                }
                            }
                        });
                    });

                    // check sub-controllers of this controller.
                    updateSubControllers(fc, val);
                }
            }
        }
    });

    if (gv.parentRecord && gv.parentRecord.parentMVC && gv.parentRecord.parentMVC.topMVC) {
        let styleProp = gv.parentRecord.parentMVC.topMVC.StyleProperties || [];
        let topFC = gv.parentRecord.parentMVC.topMVC.formController;
        if (topFC && (updateSubStyles || styleProp.indexOf(gv.property.UID) > -1)) {
            topFC.updateControlsWithStyleExpressions(true);
        }
    }
}

export function dispose(gv) {
    gv.parentRecord = null;

    tools.clearSubscriptions(gv.subs, gridValueEventTypes.valueChanged);
    tools.clearSubscriptions(gv.subs, gridValueEventTypes.writeUpdate);

    // release references we hold to properties of other DV objects.
    gv.colTextFormatObj = null;

    // dispose the apiRef, if any.
    if (gv.apiRef != null && gv.apiRef.dispose != null) {
        gv.apiRef.dispose();
    }

    gv.isDisposed = true;
}

//#region Properties called from getters/setters on the instance object...AL

export function getValue(gv) {
    if (gv.dynamicValueEdit != null) {
        return gv.dynamicValueEdit.Value;
    }
    else {
        return gv.dynamicValue.Value;
    }
}

export function setValue(gv, value) {
    //Determine if the status and value is different from the last known "saved" value...EK
    let diff = false,
        didTrans = false;

    if([PropertyDataType.DateTime, PropertyDataType.StaticDateTime].includes(gv.dynamicValue.DataType)) {
        let v1 = String(value),
            v2 = String(gv.dynamicValue.Value);
        diff = v1 !== v2;
    } else if(gv.dynamicValue.DataType === PropertyDataType.Binary) {
        let v1 = String(value),
            v2 = String(gv.dynamicValue.ValueMetadata);
        diff = v1 !== v2;
    } else {
        diff = gv.dynamicValue.Value !== value;
    }

    if (diff || (gv.dynamicValueEdit != null && gv.dynamicValueEdit.Status !== gv.dynamicValue.Status)) {
        // if the MVC is in a bulk insert mode, we do not want to store undo transactions.
        if (gv.parentRecord
            && gv.parentRecord.parentMVC) {
            didTrans = !!gv.parentRecord.parentMVC.bulkInsert;
        }

        if (gv.dynamicValueEdit == null) {
            if(didTrans) {
                if(gv.dynamicValue.DataType === PropertyDataType.Binary) {
                    doTransaction(gv, gv.dynamicValue.ValueMetadata);
                } else {
                    doTransaction(gv, gv.dynamicValue.Value);
                }
                didTrans = true;
            }
            gv.dynamicValueEdit = tools.deepClone(gv.dynamicValue);
            if (gv.dynamicValue.DataType === PropertyDataType.DateTime ||
                gv.dynamicValue.DataType === PropertyDataType.StaticDateTime) {
                if (gv.dynamicValue.Value != null) {
                    // if this is a date time value, we need to do a separate clone of the value to break the reference.
                    gv.dynamicValueEdit.Value = new Date(gv.dynamicValue.Value);
                }
            }
        }

        // only update if there really is a change...WR
        if (gv.dynamicValueEdit.Value !== value ||
            (gv.dynamicValue.DataType === PropertyDataType.Binary && gv.dynamicValueEdit.ValueMetadata !== value)) {
            // we have a new value that is not saved; track the new value.
            if (!didTrans) {
                switch (gv.dynamicValue.DataType) {
                    case PropertyDataType.Binary:
                        doTransaction(gv, gv.dynamicValueEdit.ValueMetadata);
                        break;
                    default:
                        doTransaction(gv, gv.dynamicValueEdit.Value);
                        break;
                }
                didTrans = true;
            }
            switch (gv.dynamicValue.DataType) {
                case PropertyDataType.Binary:
                    gv.dynamicValueEdit.ValueMetadata = value;
                    break;
                default:
                    gv.dynamicValueEdit.Value = value;
                    break;
            }

            if(value instanceof Date) {
                gv.dynamicValueEdit.TimeZoneOffset = 0 - ((value.getTimezoneOffset()) / 60);
            }

            //Note:  The dynamic value is not flagged as "modified" until the user actually commits it to the server (see save action) ... EK

            // If there are any expressions (in other property fields) that would be effected by this value change, this call will execute a recalc...AL
            recalcExpressions(gv);
        }
        //Else:  We really didn't change anything, don't record...EK
    } else if (gv.dynamicValueEdit != null) {
        // only update if there really is a change...WR
        if (!didTrans) {
            switch (gv.dynamicValue.DataType) {
                case PropertyDataType.Binary:
                    doTransaction(gv, gv.dynamicValueEdit.ValueMetadata);
                    break;
                default:
                    doTransaction(gv, gv.dynamicValueEdit.Value);
                    break;
            }
            didTrans = true;
        }
        // the value was reset back to the original; no effective change since last save.
        gv.dynamicValueEdit = null;

        if (gv.dynamicValue._State !== EntityStates.NewObject) {
            gv.dynamicValue._State = EntityStates.Unmodified;
        }

        // If there are any expressions (in other property fields) that would be effected by this value change, this call will execute a recalc...AL
        recalcExpressions(gv);
    }

    gv.updateUI();

    // update the client record code property if this is the code property and it has changed.
    if (gv.parentRecord != null && isRecordCode(gv)) {
        gv.parentRecord.clientRecord.Record.Code = value;

        if (gv.dynamicValueEdit == null
            && gv.dynamicValue.Status != null) {
            gv.parentRecord.clientRecord.Record.Status = gv.dynamicValue.Status;
        }
        else if (gv.dynamicValueEdit != null && gv.dynamicValueEdit.Status != null) {
            gv.parentRecord.clientRecord.Record.Status = gv.dynamicValueEdit.Status;
        }
    }

    // check if this gridValue is for a pivot view and is of an aggregate type that should update an underlying value.
    //NOTE: at this point, it is easier and as accurate to check if the parent record has pivotRecords dictionary as it is
    //        to try and access the "isPivotGrid" property of the parentRecord's parentMvc...WR
    if (gv.parentRecord != null
        && gv.parentRecord.pivotRecords != null
        && gv.parentRecord.pivotRecords.length > 0) {
        // if this is a pivot grid, we need to check if we should update the source record in some cases.
        if(gv.property.Definition.Aggregate === AggregateTypes.Min || gv.property.Definition.Aggregate === AggregateTypes.Max) {
            // we should update the source value for these aggregate types.
            if (gv.parentRecord.pivotRecords != null && gv.parentRecord.pivotRecords.length > 0) {
                // get the underlying records for this pivot property
                var pivotRecs = gv.parentRecord.pivotRecords.getVal(gv.property.PropertyID);
                if (pivotRecs != null && pivotRecs.length > 0) {
                    // now get the source property grid value object of the underlying record.
                    var gvForProperty = pivotRecs[0].gridValues.getVal(gv.property.Definition.PivotForPropertyID);
                    if (gvForProperty != null) {
                        // note we are calling directly to the set as all data manipulations should have already occurred.
                        //NOTE: this will also be within the same transaction...WR
                        setValue(gvForProperty, value);
                    }
                }
            }
        }
    }

    /**
     * This will only add a transaction action if there is a current transaction.
     * If there is no current transaction, this (usually) means that we are running
     * an UNDO ACTION and should not record an undo for the undo, etc...WR
     */
    function doTransaction(gv, origValue) {
        let mvc = null,
            monitor = null;
        if (gv
            && gv.parentRecord
            && gv.parentRecord.parentMVC) {
            mvc = gv.parentRecord.parentMVC;
        }
        if (mvc
            && mvc.transactionMonitor) {
            monitor = mvc.transactionMonitor;
        }

        if (monitor != null && monitor.currentTransaction != null) {
            monitor.addAction(TransActionTypes.ValueChange, mvc,
                function (transCallback, obj, oldValue) {
                    switch (obj.dynamicValue.DataType) {
                        case PropertyDataType.Binary:
                            setBinaryValue(obj, oldValue);
                            break;
                        default:
                            setValue(obj, oldValue);
                            break;
                    }
                    transCallback();
                }, [gv, origValue]);
        }
    }
}

export function getDocumentURI(gv) {
    var FileSize, Metadata;
    if (gv.dynamicValue.TypeValueID <= 0) {
        // the value has not been "saved" so there is no information in the service to get!
        return null;
    }
    else if (gv.isHistoricalValue === true) {
        if (gv.dynamicValue.DataType === PropertyDataType.Binary && gv.dynamicValue.ValueMetadata !== "") {
            // use a parse that supports NaN as some metdata may contain it; this would error in regular JSON.parse()
            Metadata = JSON.parseMore(gv.dynamicValue.ValueMetadata);
            let FileName;
            for (let i in Metadata) {
                if (Metadata.hasOwnProperty(i)) {
                    if (FileSize && FileName) {
                        break;
                    }
                    if (Metadata[i].Key === "FileSize") {
                        FileSize = Metadata[i].Value;
                    }
                    if (Metadata[i].Key === "FileName") {
                        FileName = Metadata[i].Value;
                    }
                }
            }

            // Ensure that the file actually has bytes before returning a location...AL
            if (FileSize !== "0") {
                // Use the baseURL only to build file download paths. This doesn't work with manually installed HTTPs certs, but does with AWS Load Balancers..PW
                return process.env.CLIENT_WEB_URL + "/DocumentServer/DownloadHistoricalFile/" + app.dv.cache.session.sessionGUID + "/" + FileName + "/" + gv.dynamicValue.TypeValueID;
                //local debugging against local service...BC
               //return "http://localhost:8080" + "/DocumentServer/DownloadHistoricalFile/" + app.dv.cache.session.sessionGUID + "/" + FileName + "/" + gv.dynamicValue.TypeValueID;
            }
            else {
                return null;
            }
        }
        else {
            return null;
        }
    }
    else {
        if (gv.dynamicValue.DataType === PropertyDataType.Binary && gv.dynamicValue.ValueMetadata !== "") {
            // use a parse that supports NaN as some metadata may contain it; this would error in regular JSON.parse()
            Metadata = JSON.parseMore(gv.dynamicValue.ValueMetadata);
            for (let i in Metadata) {
                if (Metadata[i].Key === "FileSize") {
                    FileSize = Metadata[i].Value;
                    break;
                }
            }

            // Make sure that the file hasn't been cleared before returning the URI...AL
            if (Metadata == null
                || Metadata.length === 0
                || FileSize === "0") {
                return null;
            }
                // Otherwise - proceed...AL
            else {
                // Use the baseURL only to build file download paths. This doesn't work with manually installed HTTPs certs, but does with AWS Load Balancers..PW
                //We don't need the window location host because the server mananger adds it...BC
                return process.env.CLIENT_WEB_URL + "/DocumentServer/DownloadFile/" + app.dv.cache.session.sessionGUID + "/" + gv.dynamicValue.TypeValueID.toString();
                //local debugging against local service...BC
               // return "http://localhost:8080" + "/DocumentServer/DownloadFile/" + app.dv.cache.session.sessionGUID + "/" + gv.dynamicValue.TypeValueID.toString();
            }
        }
        else {
            return null;
        }
    }
}

export function getStatus(gv) {
    if (gv.property != null
        && gv.property.Definition != null
        && gv.property.Definition.IsIgnoreRecordStatus === false
        && gv.parentRecord != null
        && gv.parentRecord.getStatus() != null
        && gv.parentRecord.getStatus().Definition.IsRecordStatus) {
        // This is a record status and it's not set to ignore the record status - use the parent record's status...AL
        return gv.parentRecord.getStatus();
        //return app.dv.cache.valueStatuses.valueStatusByValue.getVal(gv.parentRecord.status);
    }
    else {
        var dv = null;

        if (gv.dynamicValueEdit != null) {
            dv = gv.dynamicValueEdit;
        }
        else {
            dv = gv.dynamicValue;
        }

        if (dv.Status == null) {
            return app.dv.cache.valueStatuses.notInitializedStatus;
        }
        else {
            return app.dv.cache.valueStatuses.valueStatusByValue.getVal(dv.Status);
        }
    }
}

export function setStatus(gv, newStatus, suppressCalculation) {
    // Do nothing...AL
    if (gv == null
        || gv.dynamicValue == null || newStatus == null) {
        return;
    }

    // assigning the result of a calculation needs to use this function... and if it does, we
    // do not want a recursive out of stack space error having it try to calculate over and over...WR
    if (suppressCalculation == null) {
        suppressCalculation = false;
    }

    var diff = false;
    if (gv.dynamicValue.DataType === PropertyDataType.DateTime ||
        gv.dynamicValue.DataType === PropertyDataType.StaticDateTime) {
        if (gv.dynamicValueEdit != null) {
            var v1 = String(gv.dynamicValueEdit.Value), v2 = String(gv.dynamicValue.Value);
            diff = v1 != v2;
        } else {
            diff = false;
        }
    } else {
        if (gv.dynamicValueEdit != null) {
            diff = gv.dynamicValue.Value != gv.dynamicValueEdit.Value;
        } else {
            diff = false;
        }
    }

    // Determine if the status and value is different from the last known "saved" value.
    if (gv.dynamicValue.Status !== newStatus.StatusValue
        || diff) {
        // if there is not currently a dynamicValueEdit, create one for the new status.
        if (gv.dynamicValueEdit == null) {
            gv.dynamicValueEdit = tools.deepClone(gv.dynamicValue);
            if (gv.dynamicValue.DataType === PropertyDataType.DateTime ||
                gv.dynamicValue.DataType === PropertyDataType.StaticDateTime) {
                if (gv.dynamicValue.Value != null) {
                    // if this is a date time value, we need to do a separate clone of the value to break the reference.
                    gv.dynamicValueEdit.Value = new Date(gv.dynamicValue.Value);
                }
            }
        }

        // Set the status value...AL
        gv.dynamicValueEdit.Status = newStatus.StatusValue;

        //Note:  The dynamic value is not flagged as "modified" until the user actually commits it to the server (see save action) ... EK

        // If there are any expressions (in other property fields) that would be effected by this value change, this call will execute a recalc...AL
        if (!suppressCalculation) {
            recalcExpressions(gv);
        }
    } else {
        // only update if there is really a change.
        if (gv.dynamicValueEdit != null) {
            gv.dynamicValueEdit = null;

            if (gv.dynamicValue._State !== EntityStates.NewObject) {
                gv.dynamicValue._State = EntityStates.Unmodified;
            }

            // If there are any expressions (in other property fields) that would be effected by this value change, this call will execute a recalc...AL
            if (!suppressCalculation) {
                recalcExpressions(gv);
            }
        }
    }

    // Set the grid record status if this is a record code property...AL
    if (isRecordCode(gv)) {
        gv.parentRecord.setStatus(newStatus);
    }

    gv.updateUI();
}

//#endregion

class GridValueDisplayProperties {
    /**
     * Create a display properties object for the specified grid value.
     * @param {GridValue} gv
     * @class
     * @classdesc Display Properties for a Grid Value
     */
    constructor (gv) {
        this._GridValue = gv;
        /**
         * format string for formatting display value.
         * @type {string}
         */
        this.formatString = "";

        this.zBorder = null;
        this.zBackColor = null;
        this.zFontWeight = "normal";
        this.zForeColor = null;
        this.zStatusMarker = null;
    }

    /**
     * Get the value of the grid value as a text value.
     * @returns {string}
     */
    get Text () { return getTextValue(this._GridValue); }
    /**
     * Set the value of the grid value as a text value.
     * @param {string} newValue
     */
    set Text (newValue) { setTextValue(this._GridValue, newValue); }

    getText () { return getTextValue(this._GridValue); }
    setText (newValue) { setTextValue(this._GridValue, newValue); }

    /**
     * Get the value of the grid value as a boolean value.
     * @returns {boolean|null}
     */
    get CheckValue () { return getCheckValue(this._GridValue); }
    /**
     * Set the value of the grid value as a boolean value.
     * @param {boolean|null} newValue
     */
    set CheckValue (newValue) { setCheckValue(this._GridValue, newValue); }

    getCheckValue () { return getCheckValue(this._GridValue); }
    setCheckValue (newValue) { setCheckValue(this._GridValue, newValue); }

    /**
     * Toggle the value of the grid value to the next valid check value,
     * taking into account property settings for allowing Null check values.
     */
    toggleCheckValue () { toggleCheckValue(this._GridValue); }

    /**
     * Get the value of the grid value as a binary value.
     * @returns {*}
     */
    get BinaryValue () { return getBinaryValue(this._GridValue); }
    /**
     * Set the value of the grid value as a binary value.
     * @param {*} newValue
     */
    set BinaryValue (newValue) { setBinaryValue(this._GridValue, newValue); }

    getBinaryValue () { return getBinaryValue(this._GridValue); }
    setBinaryValue (newValue) { setBinaryValue(this._GridValue, newValue); }

    /**
     *  Get the text filename value of the binary value of the grid value, if any.
     * @returns {string}
     */
    get BinaryFilename () { return getBinaryFilename(this._GridValue); }

    getBinaryFilename () { return getBinaryFilename(this._GridValue); }

    /**
     * Get the value of the grid value as a numeric value.
     * @returns {number|null}
     */
    get NumberValue () { return getNumberValue(this._GridValue); }

    getNumberValue () { return getNumberValue(this._GridValue); }

    /**
     * Get the value of the grid value as a date value.
     * @returns {Date|null}
     */
    get DateValue () { return getDateValue(this._GridValue); }
    /**
     * Set the value of the grid value as a date value.
     * @param {Date|null} newValue
     */
    set DateValue (newValue) { setDateValue(this._GridValue, newValue); }

    getDateValue () { return getDateValue(this._GridValue); }
    setDateValue (newValue) { setDateValue(this._GridValue, newValue); }

    /**
     * Get the tooltip text for this grid value.
     * @returns {string|*}
     */
    getTooltipText() { return getTooltipText(this._GridValue); }

    /**
     * Get a flag to indicate if this grid value is read-only or not.
     * @returns {boolean}
     */
    isReadOnly() { return getIsReadOnly(this._GridValue); }

    /**
     * Method to reset the grid value to its original value state, if the value has been edited but not saved yet.
     */
    resetValue () { resetValue(this._GridValue); }
}

export class GridValue {
    /**
     *
     * @param {DynamicValue} dv - The base dynamic value for the data.
     * @param {number|EntityLinker} propertyID - Property identifier to pull the correct property entity from cache
     * @param {GridRecord} gridRecord - The grid record object that this value will be stored in.
     * @param {boolean} [recordIdForPivot = FALSE] - TRUE, this is for a pivot record; FALSE, this is not for a pivot record.
     */
    constructor (dv, propertyID, gridRecord, recordIdForPivot = false) {
        /**
         * Type name of this object.
         * @type {string}
         */
        this.typeName = "datavoke.entities.gridValue";

        /**
         * Types of events this object supports subscriptions for.
         * @type {{gridValueEventTypes: {valueChanged: string, writeUpdate: string}}}
         */
        this.types = { gridValueEventTypes };

        /**
         * object for event subscriptions.
         * @type {object}
         */
        this.subs = {};

        /**
         *
         */
        this.property = app.dv.cache.properties.allProperties.getVal(propertyID);
        this.parentRecord = gridRecord;

        /**
         * Flag to indicate if this is a historical (read only) value.
         * TODO: Defaulting to false right now, will change when we implement historical views...AL
         * @type {boolean}
         */
        this.isHistoricalValue = false;

        /**
         * Flag to indicate if this value should be read only.
         * @type {null}
         */
        this.forceReadOnly = null;

        /**
         * Array of UI elements that are bound to this grid value.
         * @type {Array}
         */
        this.uiElements = [];

        /**
         * Flag to indicate if the record ID is for a pivot record.
         * @type {boolean|null}
         */
        this.recordIdForPivot = recordIdForPivot || null;

        if (dv == null) {
            // We need to create a new dynamic 'not initialized' dynamic value...AL
            throw new Error('Null Dynamic value in grid Value constructor');
        }

        /**
         * The underlying value is kept here. This is the "saved" value or the value as it is in the database.
         * @type {DynamicValue}
         */
        this.dynamicValue = dv;

        /**
         * This holds a clone of the edited value when the user performs an edit (before the save - when save is performed, this will be nulled out)...AL
         * @type {DynamicValue|null}
         */
        this.dynamicValueEdit = null;

        if (dv.Value != null && dv._State !== EntityStates.Unmodified) {
            this.dynamicValueEdit = tools.deepClone(dv);
            if (dv.DataType === PropertyDataType.DateTime ||
                dv.DataType === PropertyDataType.StaticDateTime) {
                // need to explicitly create a clone of the datetime value as it is not done properly by jQuery...WR
                this.dynamicValueEdit.Value = new Date(dv.Value);
            }
        }

        /**
         * Create the display properties object for this grid value.
         * @type {GridValueDisplayProperties}
         */
        this.displayProperties = new GridValueDisplayProperties(this);

        /**
         * Flag to indicate if this object has been disposed.
         * @type {boolean}
         */
        this.isDisposed = false;
    }

    /**
     * Method to help identify this type of object.
     * @returns {string}
     */
    toString () {
        return "GridValue";
    }

    /**
     * Method to notify that the value was updated and should have visual properties recalculated.
     * @param val
     */
    writeUpdate (val) {
        writeUpdate(this, val);
    }

    /**
     * Get the status object for this grid value.
     * @returns {*}
     */
    get status () {
        return getStatus(this);
    }
    /**
     * Set the status object for this grid value.
     * @param newStatus
     */
    set status (newStatus) {
        setStatus(this, newStatus);
    }

    getStatus () {
        return getStatus(this);
    }
    setStatus (newStatus) {
        setStatus(this, newStatus);
    }

    /**
     * Get the document URI for this grid value, if applicable.
     * @returns {*}
     */
    get documentURI() {
        return getDocumentURI(this);
    }

    getDocumentURI () {
        return getDocumentURI(this);
    }

    /**
     * Get the value of the data for this grid value.
     * @returns {*}
     */
    get value () {
        return getValue(this);
    }
    /**
     * Set the value of the data for this grid value.
     * @param value
     */
    set value (value) {
        setValue(this, value);
    }

    getValue () {
        return getValue(this);
    }
    setValue (value) {
        setValue(this, value);
    }

    /**
     * Return a flag if this value is an analysis value.
     * @returns {boolean}
     */
    isAnalysisValue () {
        return getIsAnalysisValue(this);
    }

    /**
     * Return a flag if this grid value has been edited and has not been saved.
     * @returns {boolean|*}
     */
    isDirty () {
        return isDirty(this);
    }

    /**
     * Get flag indicating if grid value is a required value that has not been populated yet.
     * @returns {boolean}
     */
    isRequiredFieldEmpty () {
        return getIsRequiredFieldEmpty(this);
    }

    /**
     * Get flag indicating if grid value is a required value.
     * @returns {boolean}
     */
    isRequired () {
        return getIsRequired(this);
    }

    /**
     * Get falg indicating if grid value is to be spell checked or not.
     * @returns {boolean}
     */
    isSpellchecked () {
        return getIsSpellchecked(this);
    }

    /**
     * This function is called by a dynamic value when its value changes (edit change).
     */
    valueUpdate (dv) {
        return valueUpdate(this, dv);
    }

    /**
     * This function is called by a dynamic value when its value changes (saved change).
     */
    valueChangeDV () {
        valueChangeDV(this);
    }

    /**
     * This function is called when the grid value is to be saved.
     */
    save () {
        return save(this);
    }

    updateUI () {
        return updateUI(this);
    }

    /**
     * Another object would like to subscribe to the event specified.
     * @/// <param name="event" type="String">The name of the event to subscribe to.</param>
     * @/// <param name="src" type="Object">The requester of the event notification.</param>
     * @/// <param name="callback" type="Function">The function to call back for the event.</param>
     */
    subscribe (event, src, callback) {
        return tools.subscribe(this.subs, event, src, callback);
    }

    /**
     * Another object would like to unsubscribe from the event specified.
     * @/// <param name="event" type="String">The name of hte event to unsubscribe to.</param>
     * @/// <param name="src" type="Object">The requester of the event notification.</param>
     */
    unSubscribe (event, src) {
        return tools.unSubscribe(this.subs, event, src);
    }

    /**
     * Method to clean up and release references for this object.
     */
    dispose () {
        dispose(this);
    }
}

// The instance object of a grid value (called to create new)...AL
/**
 * Create a new grid value object.
 * @param {DynamicValue} dv
 * @param {EntityLinker|number} propertyID
 * @param {GridRecord} gridRecord
 * @param {boolean} recordIdForPivot
 * @returns {GridValue}
 */
export function create(dv, propertyID, gridRecord, recordIdForPivot) {
    return new GridValue(dv, propertyID, gridRecord, recordIdForPivot);
}
